import { useState, FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useUser } from '../contexts/UserContext'
import { homePathFor } from '../lib/postLoginRedirect'
import { Button, Card, Input, PasswordInput, Checkbox, Logo } from '../components/ui'

export const SYSTEM_NOTICE = `WARNING — This is a U.S. Government information system. Use of this system constitutes consent to monitoring, recording, and auditing for lawful purposes. Unauthorized use is prohibited and may be subject to criminal and civil penalties. All information on this system may be monitored and disclosed to law enforcement or other authorized parties. Disconnect IMMEDIATELY if you are not an authorized user.`

export default function Login() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const navigate = useNavigate()
  const { login } = useUser()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const user = await login({ username, password })
      navigate(homePathFor(user))
    } catch (err: any) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="relative flex min-h-screen w-full items-center justify-center p-4"
      style={{
        backgroundImage: 'url(/ISS_earth.avif)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Subtle scrim to deepen the view and lift the card off the photo */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />

      <Card className="relative z-10 w-full max-w-md gap-6 p-6 shadow-2xl">
        {/* Brand + heading */}
        <div className="flex flex-col items-center gap-2 text-center">
          <Logo className="mx-auto h-7 w-[231px] max-w-full" />
          <h1 className="text-base font-normal text-muted-foreground">
            Sign in to your Overmatch account
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <Input
              id="login-username"
              label="Email"
              type="text"
              autoComplete="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              className="h-11"
            />
            <PasswordInput
              id="login-password"
              label="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              className="h-11"
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          {/* System use notification + acknowledgment (NIST 3.1.9 / AC-8) */}
          <div className="flex flex-col gap-3">
            <div className="max-h-28 overflow-y-auto rounded-lg bg-muted p-3 text-xs leading-relaxed text-muted-foreground">
              {SYSTEM_NOTICE}
            </div>
            <Checkbox
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              label="I have read and acknowledge this system use notification."
            />
          </div>

          <Button
            type="submit"
            size="lg"
            className="h-11 w-full text-base"
            isLoading={loading}
            disabled={loading || !acknowledged}
          >
            {loading ? 'Logging in...' : 'Log in'}
          </Button>

          <Link
            to="/forgot-password"
            className="text-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Forgot password?
          </Link>
        </form>

        {/* Footer fine print */}
        <p className="text-center text-xs text-muted-foreground">
          DoW Acquisitions &amp; Vendor Evaluation Platform
        </p>
      </Card>
    </div>
  )
}
